import {
  isAnyField,
  isArrayField,
  isFieldType,
  isObjectField,
  isOneOfField,
} from '@shared/openapi/guards'

import type { ParameterProps } from '@features/playground.components/Parameter/Parameter'
import type { Operation } from '@features/playground.graphql'
import type { NestedRecord } from '@shared/openapi/transform/field/object'
import type { FieldsType } from '@shared/openapi/transform/field/oneOf/oneOf'
import type {
  AnyField,
  FieldExampleType,
  FieldType,
} from '@shared/openapi/transform/field/types'
import type { OperationParameter } from '@shared/openapi/types'

/**
 * Transforms a list of operation parameters into an key/value object of the parameter names and their example values.
 * @param parameters - The list of operation parameters.
 * @returns A key/value object of the parameter names and their example values.
 */
export const getExampleParametersObject = (
  parameters: OperationParameter[],
): NestedRecord<FieldExampleType> | FieldExampleType => {
  if (parameters.length === 1 && parameters[0].example !== undefined) {
    return parameters[0].example
  }

  return parameters
    .filter((parameter) => parameter.required)
    .reduce((acc, parameter) => {
      const exampleObject =
        parameter.example !== undefined
          ? { [parameter.name]: parameter.example }
          : {}
      return { ...acc, ...exampleObject }
    }, {})
}

/**
 * Transforms an operation's request body into a list of operation parameters.
 * @param operation - The operation details.
 * @returns A list of operation parameters.
 */
export const getRequestBodyProperties = (
  operation: Operation,
): OperationParameter[] => {
  if (isObjectField(operation.requestBody)) {
    return Object.entries(operation.requestBody.properties).map(
      ([name, value]) => {
        return {
          // TODO: Remove default values and come up with better type definition to ensure that anyOf is not breaking this
          deprecated: false,
          description: '',
          required: false,
          ...value,
          name,
          schema: {
            ...value,
          },
        }
      },
    )
  }

  if (isOneOfField(operation.requestBody)) {
    const requestBody = operation.requestBody.fields.find(
      (field): field is Exclude<FieldType, AnyField> => !isAnyField(field),
    )

    return requestBody
      ? [
          {
            ...requestBody,
            required: true,
            name: ('title' in requestBody && requestBody.title) || '',
            description:
              'description' in requestBody ? requestBody.description : '',
            deprecated:
              'deprecated' in requestBody ? requestBody.deprecated : false,
            schema: {
              ...requestBody,
            },
          },
        ]
      : []
  }

  return []
}

/**
 * Transforms an operation's request body into a list of transformed parameters to display.
 * @param operation - The operation details.
 * @returns A list of transformed request body parameters.
 */
export const getTransformedRequestBodyProperties = (
  operation: Operation,
): ParameterProps['childParameters'] => {
  if (isFieldType(operation.requestBody)) {
    return mapParameter('body', operation.requestBody).childParameters
  }

  return []
}

/**
 * Transforms the request body properties to a transformed request body parameter.
 * @param key - The name of the parameter.
 * @param value - The parameter object.
 * @returns A transformed request body parameter object.
 */
const mapParameter = (key: string, value: FieldType) => {
  const parameter: ParameterProps = {
    name: key,
    type: value.type,
    arrayType: undefined,
    title: 'title' in value ? value.title : undefined,
    required: 'required' in value ? value.required : false,
    content: 'description' in value ? value.description : '',
    childParameters: [],
    allowedValues: 'enum' in value ? value.enum : undefined,
  }

  if (value.type === 'oneOf' || value.type === 'anyOf') {
    parameter.childParameters = mapChildParametersFromFields(value.fields)
  }

  // if type is array, we want to map over the object properties
  if (isObjectField(value)) {
    parameter.childParameters = Object.entries(value.properties).map(
      ([pKey, pValue]) => {
        return mapParameter(pKey, pValue)
      },
    )
  }

  // if type is array, we want to flatten the child parameters
  if (isArrayField(value)) {
    const items = mapParameter('array - items', value.items)
    parameter.arrayType = items.type || undefined
    parameter.childParameters = items.childParameters
    parameter.allowedValues =
      items.childParameters && items.childParameters.length > 0
        ? parameter.allowedValues
        : items.allowedValues
  }

  return parameter
}

/**
 * Maps child parameters for types oneOf/anyOf from 'fields' attribute.
 * @param fields - The object field representing oneOf/anyOf fields.
 * @returns An array of transformed response parameters for the child fields.
 */
const mapChildParametersFromFields = (
  fields: FieldsType,
): (ParameterProps | null)[] => {
  return fields.map((field, i) => {
    if (isObjectField(field)) {
      return {
        name: `option ${i + 1}`,
        type: field.type,
        title: field.title,
        required: field.required || false,
        content: field.description || '',
        // map field attribute 'properties' to nested child parameters for object type
        childParameters: Object.entries(field.properties).map(
          ([pKey, pValue]) => {
            return mapParameter(pKey, pValue)
          },
        ),
      }
    } else if ('required' in field) {
      return {
        name: `option ${i + 1}`,
        type: field.type,
        title: field.title,
        required: field.required || false,
        content: field.description || '',
        childParameters: [],
        allowedValues: 'enum' in field ? field.enum : undefined,
      }
    }

    return null
  })
}
